// 双人互动 (two-person actions; src/office/duo.ts): stand close to someone, face each other and pause a moment, and a
// small 「互动」 bubble shows over your heads. Press E (or tap it) and pick 蹭蹭 / 击掌 / 转圈圈 (in the water: 一起漂);
// they get 「X 想和你蹭蹭」 with 好呀 (E) / 下次吧, gone after 20 s: a speech bubble over the asker's head (the page's
// card at the top when the asker isn't on screen), and the asker a small 「等 X 回应」 over theirs. The prompt, picker,
// bubbles and that card are drawn by the map (deck/duo-view.js, the engine UI kit) from overlay()'s answer each frame;
// their taps come back as tap(key). E is 好呀, Esc 下次吧. The page's own card (#duoAsk) is only for when the map
// can't draw it (no frames: the tab hidden, the map not running; the engine UI failed); the 小窗 (public/pip.js) shows
// the request too (pending(), answer()), so it isn't missed while Deck is behind other windows. If they say yes, both play it together, side by side
// and facing each other (each one's half from its own two-person sheet: looks.js duoFrames16, loaded the first time
// it's needed; the cats have their own versions), with hearts or a spark over them, for everyone to see. Walking ends it.
// The office checks everything again (near, facing, both idle, 请勿打扰, limits); agents ask and answer through the SDK
// (`deck duo`, deck accept / decline). deck/pose.js draws it (duoFrame), deck/entities.js its marks (fr.fx).
import { icon } from '/icons.js';
import { t, has, translateDom } from '/i18n.js';
import { duoReady, duoFrames16, frames16 } from '/looks.js';

/** The kinds (the office's DUO_KINDS): ms how long it plays, gap the two sprites' centres apart (px: a tile is 16),
 *  anim the sheets' animation, seq its frames over time (intro once, then loop), fx the marks over the pair, ask the
 *  icon the UI's pixel icon; the words are catalog keys by kind: duo.kind.<kind> (its name), duo.ask.<kind> (「X 想和你…」),
 *  duo.wants.<kind> (the same without the name, for the map's bubble). water: only for two in the water (the others only on land). */
export const DUO_KINDS = {
  hug: { ms: 3200, gap: 16, anim: 'duoHug', icon: 'heart', fx: 'hearts', seq: { intro: [[0, 350]], loop: [[1, 450], [2, 450], [1, 450], [3, 450]] } },
  five: { ms: 2000, gap: 20, anim: 'duoFive', icon: 'hand', fx: 'spark', seq: { intro: [[0, 450], [1, 150], [2, 350]], loop: [[3, 1000]] } },
  // 碰鼻子: retired (the office refuses new ones, E_RETIRED; not in the picker); kept so a pair that plays it still draws
  boop: { ms: 2400, gap: 16, anim: 'duoBoop', icon: 'sparkle', fx: 'heart', seq: { intro: [[0, 450], [1, 1000]], loop: [[2, 1000]] } },
  spin: { ms: 2600, gap: 18, anim: 'duoSpin', icon: 'party', fx: 'spark', spin: true, seq: { intro: [], loop: [[0, 160], [1, 160]] } },
  float: { ms: 6000, gap: 19, anim: 'duoFloat', icon: 'sea_wave', fx: 'hearts', water: true, seq: { intro: [], loop: [[0, 650], [1, 650]] } },
};
const LAND = ['hug', 'five', 'spin'];
/** The prompt: within NEAR tiles, each facing the other (within FACE sixteenths: the office allows 4), both still for PAUSE_MS. */
const NEAR = 1.6, FACE = 3, PAUSE_MS = 600, SLIDE_MS = 260;
const dir16To = (a, b) => ((Math.round((Math.atan2(b.x - a.x, b.y - a.y) / (2 * Math.PI)) * 16) % 16) + 16) % 16;
const d16 = (p) => (Number.isInteger(p.dir16) ? p.dir16 : Number.isInteger(p.dir8) ? p.dir8 * 2 : (p.dir ?? 0) * 4);
const apart = (a, b) => { const d = Math.abs(a - b) % 16; return Math.min(d, 16 - d); };
const smooth = (u) => { u = Math.max(0, Math.min(1, u)); return u * u * (3 - 2 * u); };

// ---- the pair while it plays (deck/pose.js reads it every frame). p.duo = {kind, other, lead, at, until, x, y}.
/** The office said a and b do `kind` (ms left of it: a newcomer sees the rest). */
export function setDuo(a, b, kind, ms, now = performance.now()) {
  const K = DUO_KINDS[kind]; if (!K || !a || !b || a === b || !(ms > 0)) return;
  const left = Math.min(ms, K.ms), at = now - (K.ms - left);
  a.duo = { kind, other: b, lead: true, at, until: now + left, x: a.x, y: a.y };
  b.duo = { kind, other: a, lead: false, at, until: now + left, x: b.x, y: b.y };
  a.emote = null; b.emote = null;
}
export function endDuo(p) { if (p?.duo) { const o = p.duo.other; p.duo = null; if (o?.duo?.other === p) o.duo = null; } }
/** The duo p is in now, or null: over when its time is up, or either of them moved. */
export function duoNow(p, now = performance.now()) {
  const d = p.duo; if (!d) return null;
  const o = d.other;
  if (!o) { p.duo = null; return null; } // (the office's own record, before welcome() paired it up)
  if (now >= d.until || p.moving || o.moving || p.seat || o.seat || o.duo?.other !== p || Math.hypot(p.x - d.x, p.y - d.y) > 0.3 || Math.hypot(o.x - o.duo.x, o.y - o.duo.y) > 0.3) { endDuo(p); return null; }
  return d;
}
function seqFrame(seq, t) {
  for (const [f, ms] of seq.intro) { if (t < ms) return f; t -= ms; }
  const total = seq.loop.reduce((s, [, ms]) => s + ms, 0); let x = t % total;
  for (const [f, ms] of seq.loop) { if (x < ms) return f; x -= ms; }
  return seq.loop[0][0];
}
/**
 * p's half of the duo it's in (deck/pose.js otterFrame), or null.
 * @returns {{frame, duo: boolean, wet, ox: 0, oy: 0, gx, gy, dz, kind: 'duo', emote: null, fx?}} frame: in the duo sheet
 *   (duo true), or (not loaded yet) the main sheet's stand facing the partner; gx/gy: where it stands for it, from where
 *   it is (tiles); dz: depth (the one who asked in front); fx: the marks over the pair (only on the one who asked)
 */
export function duoFrame(p, now, look) {
  const d = duoNow(p, now); if (!d) return null;
  const K = DUO_KINDS[d.kind], o = d.other, t = now - d.at, T = d.until - d.at;
  const left = p.drawX < o.drawX || (p.drawX === o.drawX && d.lead);
  const cx = (p.drawX + o.drawX) / 2, cy = (p.drawY + o.drawY) / 2, half = K.gap / 32;
  const e = smooth(t / SLIDE_MS) * smooth((T - t) / SLIDE_MS); // sliding into place and back
  let tx = cx + (left ? -half : half), ty = cy, face = left ? 4 : 12;
  if (K.spin) { // round each other once, holding paws (the pair's line turning on a flattened circle)
    const u = smooth((t - SLIDE_MS) / Math.max(1, T - 2 * SLIDE_MS)), a = u * Math.PI * 2 + (left ? Math.PI : 0);
    tx = cx + Math.cos(a) * half; ty = cy - Math.sin(a) * half * 0.55;
    face = dir16To({ x: tx, y: ty }, { x: 2 * cx - tx, y: 2 * cy - ty });
  }
  const gx = (tx - p.drawX) * e, gy = (ty - p.drawY) * e;
  const f = duoFrames16(K.anim, face), ready = !!f && duoReady(look), stand = frames16('idle', face < 8 ? 3 : 13)[0];
  const frame = ready ? f[seqFrame(K.seq, t)] : stand;
  const fx = d.lead && K.fx ? { kind: K.fx, t, T, x: cx + 0.5, y: cy } : null;
  return { frame, stand, duo: ready, wet: !!p.boat, ox: 0, oy: 0, gx, gy, dz: d.lead ? 0.01 : 0, kind: 'duo', emote: null, ...(fx && { fx }) };
}

// ---- the page: only the request card at the top for when the map can't draw (the rest is drawn on the map)
const CSS = `#duoAsk{position:fixed;left:50%;top:64px;transform:translateX(-50%);z-index:18;display:none;flex-direction:column;gap:6px;width:250px;max-width:calc(100vw - 24px);box-sizing:border-box;background:#fff8e7;border:3px solid #1b1410;border-radius:14px;box-shadow:0 3px 0 #1b1410;padding:8px 10px;font:14px/1.4 var(--ui-font,-apple-system,system-ui,sans-serif);color:#1b1410;animation:duoPop .25s ease-out}
#duoAsk .t{display:flex;align-items:center;gap:6px;word-break:break-word}
#duoAsk .row{display:flex;gap:6px;justify-content:flex-end}
#duoAsk button{font:inherit;border:2px solid #1b1410;border-radius:999px;padding:3px 12px;background:#fff;cursor:pointer;color:#1b1410}
#duoAsk button.yes{background:#FDD03B}
#duoAsk kbd{font:10px system-ui;border:1px solid #1b1410;border-radius:2px;padding:0 3px;margin-left:2px;background:#fff}
#duoLive{position:fixed;left:-9999px;width:1px;height:1px;overflow:hidden}
#duoAsk .duoTime{height:4px;background:#e9d8b4;border-radius:2px;overflow:hidden}#duoAsk .duoTime i{display:block;height:100%;background:#1b1410;width:100%}
@keyframes duoPop{from{opacity:0}to{opacity:1}}
@media (hover:none){#duoAsk kbd{display:none}}`;
const touch = () => matchMedia('(hover: none)').matches;
/** The page's menus and panels that Esc closes (any of them open: Esc is theirs, not 下次吧). Not the person card
 *  (#greetbox): it's up whenever someone is near, as they are for this. */
const PANELS = '#tour .tcard, #dress-panel, #mbpick, #gboard, #settings, #guestDlg, #adConfirm, #statusMenu, #emoteMenu, #wb, #meet, #fishMode, #fishBook, #map-editor, #gmpanel, .dd-list, .overlay';
const panelOpen = () => [...document.querySelectorAll(PANELS)].some((el) => { const r = el.getBoundingClientRect(); return r.width * r.height > 0 && getComputedStyle(el).visibility !== 'hidden'; });
/** How long without a frame drawn (the tab hidden, the map not running) before the request goes back to the top card. */
const STALE_MS = 500;

/**
 * @param o {me, peers, send, toast, lookOf, status(p), typing(): boolean, eTaken(): boolean}: the app's state; status(p)
 *   (public/status.js statusOf): someone on 请勿打扰 isn't offered (the office refuses it, E_DND); eTaken: E sits down or
 *   opens a 白板 here (public/sit.js, whiteboard.js), so the prompt doesn't show the key (a tap still opens it)
 * @returns {{msg(m), key(): boolean, open(): boolean, overlay(cam, now, drawn), tap(key), welcome(me, peers), clear(),
 *   pending(), answer(yes)}}
 */
export function initDuo(o) {
  const st = document.createElement('style'); st.textContent = CSS; document.head.appendChild(st);
  const card = document.createElement('div'); card.id = 'duoAsk';
  card.innerHTML = '<span class="t"></span><div class="duoTime"><i></i></div><div class="row"><button class="no"><span data-i18n="duo.no"></span> <kbd>Esc</kbd></button><button class="yes"><span data-i18n="duo.yes"></span> <kbd>E</kbd></button></div>';
  // what a screen reader says when a request comes (the map's bubble isn't in the page)
  const said = Object.assign(document.createElement('div'), { id: 'duoLive' }); said.setAttribute('aria-live', 'polite'); said.setAttribute('role', 'status');
  document.body.append(card, said);
  translateDom(card); // (and again by itself on a language change)

  // partner: who the prompt is for; ask: a request to me {id, from, kind, name, agent, until, ms, shown}; sent: mine
  // {id, to, kind, toName, until, ms}; prompted: the prompt was drawn last frame (E opens the picker then)
  let partner = null, picking = false, prompted = false, ask = null, askTimer = 0, asking = 0, sent = null;
  // tried: the kind I last asked for or answered (an E_WATER refusal is worded by it: float needs water, the rest land)
  let tried = null;
  let framed = 0, lastCam = null, drawable = true, covered = false; // when overlay() last ran (no frames: the tab is hidden, the map isn't drawn), its camera, and whether the map can draw the UI
  const still = new WeakMap(); // otter -> {x, y, dir, since}: how long it has stood still, facing the same way

  const stillFor = (p, now) => {
    let s = still.get(p);
    if (!s || p.moving || Math.abs(p.x - s.x) > 0.05 || Math.abs(p.y - s.y) > 0.05 || d16(p) !== s.dir) still.set(p, (s = { x: p.x, y: p.y, dir: d16(p), since: now }));
    return p.moving ? 0 : now - s.since;
  };
  /** Who I could do one with right now (the nearest), or null. */
  function candidate(now) {
    const me = o.me(); if (!me || me._seat || duoNow(me, now) || o.status?.(me)?.s === 'dnd') return null;
    const mine = stillFor(me, now);
    let best = null, bd = Infinity;
    for (const p of o.peers().values()) {
      const pd = Math.hypot(p.x - me.x, p.y - me.y), ps = stillFor(p, now);
      if (pd > NEAR || pd >= bd || !!p.boat !== !!me.boat || p._seat || duoNow(p, now) || o.status?.(p)?.s === 'dnd') continue;
      if (Math.min(mine, ps) < PAUSE_MS) continue;
      // facing each other; an agent is turned by the office when it asks or answers, so only I need to face it
      if (apart(d16(me), dir16To(me, p)) > FACE || (p.kind !== 'agent' && apart(d16(p), dir16To(p, me)) > FACE)) continue;
      best = p; bd = pd;
    }
    return best;
  }
  const kindsFor = (p) => (p.boat ? ['float'] : LAND);

  function openPick() {
    const p = partner; if (!p) return;
    const kinds = kindsFor(p);
    if (kinds.length === 1) { askFor(p, kinds[0]); return; }
    picking = true;
    for (const s of [o.lookOf(o.me()), o.lookOf(p)]) duoReady(s); // start loading the sheets now
  }
  function closePick() { picking = false; }
  const choose = (k) => { if (partner && kindsFor(partner).includes(k)) askFor(partner, k); closePick(); };
  function askFor(p, kind) {
    o.send({ t: 'duo_ask', to: p.id, kind }); tried = kind;
    duoReady(o.lookOf(o.me())); duoReady(o.lookOf(p));
    // no word back at all (an office from before this, e.g. after a rollback): say so rather than nothing
    clearTimeout(asking); asking = setTimeout(() => o.toast(t('duo.notSent')), 6000);
  }

  function showAsk(m) {
    const K = DUO_KINDS[m.kind]; if (!K) return;
    ask = { id: m.id, from: m.from, kind: m.kind, name: m.name || t('duo.otter'), agent: m.fromKind === 'agent', until: Date.now() + (m.ms ?? 20_000), ms: m.ms ?? 20_000, shown: Date.now() };
    const text = t(`duo.ask.${m.kind}`, { name: ask.name });
    said.textContent = touch() ? text : t('duo.askKeys', { ask: text });
    const el = card.querySelector('.t');
    el.replaceChildren(...(ask.agent ? [icon('robot')] : []), document.createTextNode(text), icon(K.icon));
    const p = o.peers().get(m.from); if (p) duoReady(o.lookOf(p));
    duoReady(o.lookOf(o.me()));
    // drawn by the map (over their head, or its card at the top); the page's card if the map can't
    card.style.display = live() ? 'none' : 'flex';
    tick();
  }
  function hideAsk() { ask = null; covered = false; card.style.display = 'none'; said.textContent = ''; tick(); }
  /** The map is drawing frames (overlay() ran just now) and can draw the request. */
  const live = () => drawable && !covered && !!lastCam && performance.now() - framed < STALE_MS;
  /** The countdown bars (and, with no frames drawn for a while, the request back to the top card). */
  function tick() {
    clearInterval(askTimer); askTimer = 0;
    if (!ask && !sent) return;
    const run = () => {
      const now = Date.now();
      if (ask && ask.until <= now) hideAsk();
      if (sent && sent.until <= now) hideWait();
      if (!ask && !sent) return;
      if (ask && !live()) card.style.display = 'flex'; // no frames: the map can't show it
      if (ask) card.querySelector('.duoTime i').style.width = `${Math.round((100 * (ask.until - now)) / ask.ms)}%`;
    };
    run(); if (ask || sent) askTimer = setInterval(run, 200);
  }
  function hideWait() { sent = null; tick(); }

  // ---- the bubbles over a head (deck/duo-view.js draws them; screen pixels)
  /** Where p is on screen: x its middle, top over its name tag (and an emote's icon on it: deck/entities.js), bottom
   *  its feet; null when it's off screen (or not here). */
  function headOf(p, cam) {
    if (!p || !cam || document.hidden) return null;
    const S = cam.S(), x = cam.x + (p.drawX + 0.5) * S, feet = cam.y + (p.drawY + 1) * S;
    const top = feet - 30 * cam.scale - 18 + (p._tagY ?? 0) * S - (p.emote ? 34 : 0);
    if (x < 0 || x > (cam.width ?? innerWidth) || feet < 0 || top > (cam.height ?? innerHeight)) return null;
    return { x, top, bottom: feet };
  }
  const answer = (yes) => { if (!ask) return; o.send({ t: 'duo_answer', id: ask.id, accept: yes }); tried = ask.kind; hideAsk(); };
  card.querySelector('.yes').onclick = () => answer(true);
  card.querySelector('.no').onclick = () => answer(false);

  // Esc: 下次吧 to a request, but only as the last word: after every other Esc (bubbling, not taken: menus, cards,
  // panels close first) and not while one of the page's panels is open (a second Esc then); not in its first moments
  addEventListener('keydown', (e) => {
    if (!ask || e.key !== 'Escape' || e.defaultPrevented || e.repeat || e.metaKey || e.ctrlKey || e.altKey || e.isComposing || o.typing?.() || panelOpen()) return;
    if (Date.now() - ask.shown < 400) return;
    e.preventDefault(); answer(false);
  });
  // 1-4 pick while the picker is open (not the 动作 keys then), Esc closes it
  addEventListener('keydown', (e) => {
    if (e.metaKey || e.ctrlKey || e.altKey || e.isComposing || o.typing?.()) return;
    if (!picking) return;
    if (e.key === 'Escape') { e.preventDefault(); e.stopImmediatePropagation(); closePick(); return; }
    const k = partner && /^[1-9]$/.test(e.key) ? kindsFor(partner)[Number(e.key) - 1] : null;
    if (k) { e.preventDefault(); e.stopImmediatePropagation(); if (!e.repeat) choose(k); }
  }, true);

  /** The words for the office's refusal codes: duo.err.<code> (E_LEFT: an E_GONE because they left); null for one this page doesn't know. */
  const ERR = (c) => (c && has(`duo.err.${c}`) ? t(`duo.err.${c}`) : null);
  /** How long to wait, from the office's retry_ms: 「3 秒」, 「1 分钟」 (null: it didn't say). */
  const wait = (ms) => (!(ms > 0) ? null : ms < 60_000 ? t('duo.wait.sec', { n: Math.max(1, Math.ceil(ms / 1000)) }) : t('duo.wait.min', { n: Math.ceil(ms / 60_000) }));
  // which case of its code (m.kind: duo.ts DuoWhy); an office from before says it only in its English message
  const why = (m) => m.kind ?? (m.code === 'E_RATE' ? (/said no/.test(m.message ?? '') ? 'declined' : 'many') : m.code === 'E_GONE' ? (/ isn't here/.test(m.message ?? '') ? 'left' : 'ended')
    : m.code === 'E_WATER' ? (DUO_KINDS[tried]?.water ? 'float' : 'land') : /^(you|turn)/.test(m.message ?? '') ? (m.code === 'E_NOT_FACING' ? 'turn' : 'self') : 'them');
  const rateText = (m) => {
    const w = wait(m.retry_ms);
    if (why(m) === 'declined') return w ? t('duo.rate.declinedWait', { wait: w }) : t('duo.rate.declined');
    return w ? t('duo.rate.manyWait', { wait: w }) : t('duo.rate.many');
  };
  const errText = (m) => m.code === 'E_RATE' ? rateText(m) : m.code === 'E_PENDING' && why(m) === 'self' && wait(m.retry_ms) ? t('duo.err.E_PENDING_wait', { wait: wait(m.retry_ms) }) : m.code === 'E_GONE' && why(m) === 'left' ? ERR('E_LEFT') : m.code === 'E_DND' ? t(why(m) === 'self' ? 'duo.err.E_DND_self' : 'duo.err.E_DND')
    : m.code === 'E_NOT_FACING' ? t(why(m) === 'turn' ? 'duo.err.E_NOT_FACING_turn' : 'duo.err.E_NOT_FACING') : m.code === 'E_WATER' && why(m) === 'float' ? t('duo.err.E_WATER_float')
    : m.code === 'E_BUSY' && m.kind && m.name ? t('duo.busy', { kind: m.kind, name: m.name })
    : ERR(m.code) ?? t('duo.failed');

  const api = {
    /** E, first: answer the request on screen (yes; its card says E), or close the picker; false: nothing of mine open. */
    key() {
      // (not in its first moments: an E pressed to sit or stand as it pops up isn't a yes to a card nobody read)
      if (ask) { if (Date.now() - ask.shown >= 400) answer(true); return true; }
      if (picking) { closePick(); return true; }
      return false;
    },
    /** E, after sitting down and the 白板 had their turn: open the picker over the prompt. */
    open() {
      if (partner && prompted && !picking) { openPick(); return true; }
      return false;
    },
    /** Every frame (deck/scene.js): who I could do one with, and what deck/duo-view.js draws: the prompt or picker over
     *  our heads, a request to me over the asker's head, mine over the head of the one I asked (null: nothing). */
    overlay(cam, now, drawn = true) {
      framed = performance.now(); lastCam = cam; drawable = drawn;
      const out = { barh: parseFloat(document.documentElement.style.getPropertyValue('--barh')) || 0, tap: (k) => api.tap(k) }, T = touch(), wall = Date.now();
      if (ask) {
        card.style.display = drawn && !covered ? 'none' : 'flex'; // the map draws it (over their head, or its card at the top when they're off screen)
        const p = o.peers().get(ask.from), K = DUO_KINDS[ask.kind];
        // covered (b): where the map would draw it, something of the page is on top (a screen share, a menu): the page card then
        out.ask = { covered: (b) => { if (b !== covered) { covered = b; card.style.display = drawn && !b ? 'none' : 'flex'; } }, at: headOf(p, cam), p, name: ask.name, text: t(`duo.wants.${ask.kind}`), icon: K.icon, agent: ask.agent, left: (ask.until - wall) / ask.ms, ms: ask.ms };
      }
      if (sent) {
        const p = o.peers().get(sent.to), at = headOf(p, cam);
        if (at) out.wait = { at, p, icon: DUO_KINDS[sent.kind]?.icon, text: t('duo.waiting', { name: sent.toName }), left: (sent.until - wall) / sent.ms, ms: sent.ms };
      }
      if (!drawn) { if (partner || picking) { partner = null; closePick(); } prompted = false; return null; } // (no prompt or picker the map can't show)
      // (a request open, mine or theirs: no prompt; E answers theirs, and the office would refuse another of mine)
      const p = ask || sent ? null : candidate(now), me = o.me();
      if (p !== partner) { partner = p; if (!p) closePick(); }
      prompted = false;
      if (p && me) {
        const S = cam.S(), x = cam.x + ((me.drawX + p.drawX) / 2 + 0.5) * S, bottom = cam.y + Math.min(me.drawY, p.drawY) * S + S - 30 * cam.scale - 26;
        if (picking) out.pick = { x, bottom: bottom + 4, me, p, kinds: kindsFor(p).map((k) => ({ kind: k, name: t(`duo.kind.${k}`), icon: DUO_KINDS[k].icon })), keys: !T };
        else { out.prompt = { x, bottom, me, p, key: !o.eTaken?.() }; prompted = true; } // eTaken: E sits / opens a 白板 here, tap it instead
      }
      return out.ask || out.wait || out.prompt || out.pick ? out : null;
    },
    /** A tap or click on what deck/duo-view.js drew (its key: yes, no, prompt, pick:<kind>; the boxes themselves: nothing). */
    tap(key) {
      if (key === 'yes' || key === 'no') answer(key === 'yes');
      else if (key === 'prompt') { if (picking) closePick(); else openPick(); }
      else if (key?.startsWith('pick:')) choose(key.slice(5));
    },
    /** The request to me, for the 小窗 (public/pip.js): {id, text, icon, agent, left (0..1)} or null. */
    pending() {
      if (!ask) return null;
      const K = DUO_KINDS[ask.kind];
      return { id: ask.id, text: t(`duo.ask.${ask.kind}`, { name: ask.name }), icon: K.icon, agent: ask.agent, left: Math.max(0, (ask.until - Date.now()) / ask.ms) };
    },
    /** 好呀 (true) / 下次吧 (false) to the request to me (the 小窗's buttons). */
    answer(yes) { answer(!!yes); },
    /** The welcome's peers: pairs under way when I arrived (each peer's `duo`: {with, kind, lead, ms}). */
    welcome(me, peers) {
      const all = [...peers.values(), me].filter(Boolean);
      for (const p of all) if (p.duo?.lead && p.duo.with && !p.duo.other) {
        const q = all.find((x) => x.id === p.duo.with), w = p.duo;
        p.duo = null; if (q) { q.duo = null; setDuo(p, q, w.kind, w.ms); }
      }
      for (const p of all) if (p.duo && !p.duo.other) p.duo = null;
    },
    msg(m) {
      const find = (id) => (id === o.me()?.id ? o.me() : o.peers().get(id));
      if (m.t === 'duo_play') {
        if (m.a === o.me()?.id || m.b === o.me()?.id) { clearTimeout(asking); if (sent) hideWait(); }
        const a = find(m.a), b = find(m.b); if (!a || !b) return;
        setDuo(a, b, m.kind, m.ms);
        duoReady(o.lookOf(a)); duoReady(o.lookOf(b));
        if (ask && (m.a === ask.from || m.b === ask.from)) hideAsk();
      } else if (m.t === 'duo_end') { endDuo(find(m.a)); endDuo(find(m.b)); }
      else if (m.t === 'duo_ask') showAsk(m);
      else if (m.t === 'duo_gone') { if (ask && (!m.id || m.id === ask.id)) hideAsk(); if (m.code) o.toast(errText(m)); } // (code: my answer was refused)
      else if (m.t === 'duo_sent') {
        clearTimeout(asking); const K = DUO_KINDS[m.kind], ms = m.ms > 0 ? m.ms : 20_000;
        sent = { id: m.id, to: m.to, kind: m.kind, toName: m.toName || t('duo.someone'), until: Date.now() + ms, ms };
        tick();
        // over their head when they're on screen; else (or no frames drawn) the toast, as before
        if (!(live() && headOf(o.peers().get(m.to), lastCam))) o.toast(K ? t('duo.sent', { name: m.toName, what: t(`duo.kind.${m.kind}`) }) : t('duo.sentPlain', { name: m.toName }), null, K?.icon);
      }
      else if (m.t === 'duo_err') { clearTimeout(asking); o.toast(errText(m)); }
      else if (m.t === 'duo_status') {
        if (sent && (!m.id || m.id === sent.id)) hideWait();
        const n = m.toName || t('duo.someone');
        o.toast(m.status === 'declined' ? t('duo.status.declined', { name: n }) : m.status === 'expired' ? t('duo.status.expired', { name: n }) : m.status === 'left' ? t('duo.status.left', { name: n })
          : t(m.message?.startsWith('they') || m.message?.startsWith('too') ? 'duo.status.failedBusy' : 'duo.status.failedChanged'));
      }
    },
    /** A new connection (reconnect): requests and the prompt start over. */
    clear() { hideAsk(); hideWait(); closePick(); clearTimeout(asking); },
    shown: () => !!ask || picking,
  };
  return api;
}
